import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { rem } from 'polished'
import type { PropsWithChildren, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { _searchTileContainerQueryUp } from './_SearchTileBase'
import { searchTileContentSpacingStyles } from './searchTileLayoutStyles'

const Container = styled.div<{ $density: SearchTileDensity }>`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-height: ${props => props.$density === 'compact' ? rem(208) : 'auto'};
  ${searchTileContentSpacingStyles}
`

const ContentContainer = styled.div<{ $density: SearchTileDensity }>`
  flex-grow: 1;
  grid-area: content;
  display: grid;
  min-width: 0;
  gap: ${props => props.$density === 'compact' ? rem(12) : rem(16)};
  ${props => props.$density === 'compact' ?
    css`
      grid-template:
        "content-header" auto
        "content-primary" auto
        "content-secondary" auto / 1fr;
      align-items: start;
    ` :
    css`
      grid-template:
        "content-primary" auto
        "content-secondary" min-content / 1fr;
    `}

  & > * {
    min-width: 0;
  }

  ${_searchTileContainerQueryUp.tablet} {
    ${props => props.$density === 'compact' ?
      css`
        grid-template:
          "content-header" auto
          "content-primary" 1fr
          "content-secondary" auto / 1fr;
        align-items: start;
      ` :
      css`
        grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(200)};
      `}
  }

  ${_searchTileContainerQueryUp.desktopSmall} {
    ${props => props.$density === 'compact' ?
      css`
        grid-template:
          "content-header" auto
          "content-primary" auto
          "content-secondary" auto / 1fr;
      ` :
      css`
        grid-template:
          "content-primary" auto
          "content-secondary" min-content / 1fr;
      `}
  }

  ${_searchTileContainerQueryUp.desktop} {
    ${props => props.$density === 'compact' ?
      css`
        grid-template:
          "content-header" auto
          "content-primary" 1fr
          "content-secondary" auto / 1fr;
      ` :
      css`
        grid-template: "content-primary content-secondary" 1fr / minmax(0, 1fr) ${rem(200)};
      `}
  }
`

const Extension = styled.div`
  &:empty {
    display: none;
  }
`

interface Props extends PropsWithChildren {
  /**
   * Supporting content that always sits at the bottom of the content
   */
  bottomExtension?: ReactNode
  /**
   * Supporting content that always sits at the top of the content
   */
  topExtension?: ReactNode
  /**
   * @default default
   */
  density?: SearchTileDensity
}

function _SearchTileContent({ children, bottomExtension, topExtension, density = 'default' }: Props) {
  return <Container $density={density}>
    {!!topExtension && <Extension>{topExtension}</Extension>}
    <ContentContainer $density={density} className={_SearchTileContent.name}>
      {children}
    </ContentContainer>
    {!!bottomExtension && <Extension>{bottomExtension}</Extension>}
  </Container>
}

export default _SearchTileContent
